/**
 * O contrato da consulta pública de credenciados.
 *
 * A VIEW `public.credenciados_publico` EXPÕE SEIS COLUNAS, e são estas: `id`,
 * `nome`, `nome_fantasia`, `tipo`, `status` e `dre_id`. Nada de CPF, CNPJ,
 * telefone, e-mail ou endereço — a consulta é aberta a qualquer pessoa da
 * internet, e a minimização é exigência de `docs/LGPD-E-ACESSIBILIDADE.md`, não
 * preferência de tela. Acrescentar campo aqui é acrescentar vazamento: se a
 * coluna não está na view, ela não existe para esta tela.
 *
 * A view também já recorta os status: só `ATIVO`, `SUSPENSO` e `BLOQUEADO`
 * aparecem. `INATIVO` fica de fora — quem saiu do programa não é mais assunto
 * público.
 *
 * Sem React aqui.
 */

import type { StatusTone } from '@/nucleo/statusBadgeTokens';
import type { StatusVariant } from '@/ui/StatusBadge';

/** Uma diretoria regional, como `GET /bff/v1/consulta-publica/dres` devolve. */
export interface Dre {
  id: string;
  codigo: string;
  nome: string;
}

/** Uma página do recorte: os itens desta página e o total do recorte inteiro. */
export interface Pagina<T> {
  itens: T[];
  total: number;
  pagina: number;
  tamanho: number;
}

/* ──────────────────────────── Tipo de pessoa ────────────────────────────── */

/** `public.tipo_pessoa`, como a coluna `credenciados.tipo` guarda. */
export const TIPOS_DE_CREDENCIADO = ['PF', 'PJ', 'COOPERATIVA'] as const;
export type TipoDeCredenciadoPublico = (typeof TIPOS_DE_CREDENCIADO)[number];

export const ROTULO_DO_TIPO: Record<TipoDeCredenciadoPublico, string> = {
  PF: 'Pessoa física',
  PJ: 'Pessoa jurídica',
  COOPERATIVA: 'Cooperativa',
};

/** Opções do filtro de tipo, já com o "Todos" que a tela usa como padrão. */
export const OPCAO_TODOS = 'TODOS' as const;

export const OPCOES_DE_TIPO: readonly {
  value: TipoDeCredenciadoPublico | 'TODOS';
  label: string;
}[] = [
  { value: OPCAO_TODOS, label: 'Todos' },
  { value: 'PF', label: ROTULO_DO_TIPO.PF },
  { value: 'PJ', label: ROTULO_DO_TIPO.PJ },
  { value: 'COOPERATIVA', label: ROTULO_DO_TIPO.COOPERATIVA },
];

/** Rótulo do tipo, com o valor cru como último recurso. */
export function rotuloDoTipo(valor: string | null | undefined): string {
  if (!valor) {
    return '—';
  }
  return ROTULO_DO_TIPO[valor as TipoDeCredenciadoPublico] ?? valor;
}

/* ───────────────────────── Status público ───────────────────────────────── */

/** Os três status que a view deixa passar. */
export const STATUS_PUBLICOS = ['ATIVO', 'SUSPENSO', 'BLOQUEADO'] as const;
export type StatusPublico = (typeof STATUS_PUBLICOS)[number];

/** Rótulo, selo e tom de cada status — as variantes que `StatusBadge` já tem. */
export interface ApresentacaoDoStatusPublico {
  rotulo: string;
  descricao: string;
  variante: StatusVariant;
  tom: StatusTone;
}

export const APRESENTACAO_DO_STATUS_PUBLICO: Record<StatusPublico, ApresentacaoDoStatusPublico> = {
  ATIVO: {
    rotulo: 'Ativo',
    descricao: 'Credenciado apto a atender o programa.',
    variante: 'ativo',
    tom: 'success',
  },
  SUSPENSO: {
    rotulo: 'Suspenso',
    descricao: 'Atendimento suspenso temporariamente.',
    variante: 'suspenso',
    tom: 'warning',
  },
  BLOQUEADO: {
    rotulo: 'Bloqueado',
    descricao: 'Bloqueado por pendência administrativa ou financeira.',
    variante: 'bloqueado',
    tom: 'destructive',
  },
};

/**
 * Apresentação de um status que a API devolveu, inclusive um que este front
 * ainda não conhece.
 *
 * `credenciados_publico.status` é coluna de banco, e a lista de valores muda
 * com a regra de negócio antes de mudar aqui — a interface de referência já
 * trazia um `INATIVO` que este contrato não tem. Indexar o mapa direto quebra
 * a tela inteira no primeiro valor novo; o padrão neutro mostra o valor como
 * veio e mantém a lista de pé.
 */
export function apresentacaoDoStatusPublico(valor: string): ApresentacaoDoStatusPublico {
  return (
    APRESENTACAO_DO_STATUS_PUBLICO[valor as StatusPublico] ?? {
      rotulo: valor,
      descricao: 'Situação não prevista nesta versão da consulta.',
      variante: 'inativo',
      tom: 'neutral',
    }
  );
}

/** Rótulo do status, com o valor cru como último recurso. */
export function rotuloDoStatusPublico(valor: string | null | undefined): string {
  if (!valor) {
    return '—';
  }
  return APRESENTACAO_DO_STATUS_PUBLICO[valor as StatusPublico]?.rotulo ?? valor;
}

/** Opções do filtro de status, com o "Todos" primeiro. */
export const OPCOES_DE_STATUS_PUBLICO: readonly {
  value: StatusPublico | 'TODOS';
  label: string;
}[] = [
  { value: OPCAO_TODOS, label: 'Todos' },
  ...STATUS_PUBLICOS.map((status) => ({
    value: status,
    label: APRESENTACAO_DO_STATUS_PUBLICO[status].rotulo,
  })),
];

/* ──────────────────────────── Linha e filtro ────────────────────────────── */

/** Uma linha de `public.credenciados_publico`. Seis colunas, nem uma a mais. */
export interface CredenciadoPublico {
  id: string;
  nome: string;
  nome_fantasia: string | null;
  tipo: TipoDeCredenciadoPublico;
  status: StatusPublico;
  dre_id: string | null;
}

/**
 * O recorte da consulta.
 *
 * TODO O FILTRO É DO SERVIDOR, inclusive a busca por nome e a paginação. Recortar
 * no navegador depois de a página chegar faz o total contar o que veio, não o que
 * sobrou, e a paginação andar em blocos irregulares (CLAUDE.md, "Regras de API",
 * item 6).
 */
export interface FiltroDeCredenciadosPublicos {
  /** Busca por nome ou nome fantasia. */
  busca: string;
  tipo: TipoDeCredenciadoPublico | 'TODOS';
  status: StatusPublico | 'TODOS';
  dre_id: string | 'TODOS';
  /** Começa em 1. */
  pagina: number;
  tamanho: number;
}

/** O tamanho de página do protótipo — 20 cartões por vez. */
export const TAMANHO_DA_PAGINA = 20;

export const FILTRO_INICIAL: FiltroDeCredenciadosPublicos = {
  busca: '',
  tipo: OPCAO_TODOS,
  status: OPCAO_TODOS,
  dre_id: OPCAO_TODOS,
  pagina: 1,
  tamanho: TAMANHO_DA_PAGINA,
};

/** Verdadeiro quando algum recorte está aplicado — decide a mensagem do vazio. */
export function temFiltroAplicado(filtro: FiltroDeCredenciadosPublicos): boolean {
  return (
    filtro.busca.trim() !== '' ||
    filtro.tipo !== OPCAO_TODOS ||
    filtro.status !== OPCAO_TODOS ||
    filtro.dre_id !== OPCAO_TODOS
  );
}

/** `EN — DRE Exemplo Norte`, ou travessão quando a diretoria não é conhecida. */
export function rotuloDaDre(
  dreId: string | null | undefined,
  dres: readonly { id: string; codigo: string; nome: string }[] | undefined,
): string {
  if (!dreId || !dres) {
    return '—';
  }
  const dre = dres.find((item) => item.id === dreId);
  return dre ? `${dre.codigo} — ${dre.nome}` : '—';
}
